function stringField(value, fallback = '') {
    return typeof value === 'string' && value ? value : fallback;
}
function optionalStringField(value) {
    return typeof value === 'string' && value ? value : undefined;
}
function normalizeDelimitedField(value) {
    if (Array.isArray(value)) {
        const values = value
            .filter((entry) => typeof entry === 'string' && Boolean(entry));
        return values.length ? values.join(',') : undefined;
    }
    return optionalStringField(value);
}
export function normalizeContact(raw) {
    return {
        avatarColor: optionalStringField(raw.avatarColor || raw.avatar_color),
        deletedAt: optionalStringField(raw.deletedAt || raw.deleted_at),
        groupIds: normalizeDelimitedField(raw.groupIds || raw.group_ids),
        groupNames: normalizeDelimitedField(raw.groupNames || raw.group_names),
        handle: optionalStringField(raw.handle),
        handleUpdatedAt: optionalStringField(raw.handleUpdatedAt || raw.handle_updated_at),
        handleVisibility: optionalStringField(raw.handleVisibility || raw.handle_visibility),
        id: String(raw.id),
        name: stringField(raw.name || raw.displayName || raw.handle || raw.email || raw.phoneNumber || raw.phone_number, 'Unknown'),
        phoneNumber: optionalStringField(raw.phoneNumber || raw.phone_number),
        email: optionalStringField(raw.email),
        lastCalled: optionalStringField(raw.lastCalled || raw.last_called),
        linkedClientId: optionalStringField(raw.linkedClientId || raw.linked_client_id),
        notes: optionalStringField(raw.notes),
        organization: optionalStringField(raw.organization),
        isFavorite: Boolean(raw.isFavorite ?? raw.is_favorite),
        source: optionalStringField(raw.source)
    };
}
const CONTACT_ID = /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i;
function isRecord(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
function isDelimitedField(value) {
    return value == null || typeof value === 'string'
        || (Array.isArray(value) && value.every((item) => typeof item === 'string'));
}
/** Validate server records before the permissive legacy/cache mapper. */
export function checkedContact(value, expectedId) {
    if (!isRecord(value) || typeof value.id !== 'string' || !value.id.trim() || typeof value.name !== 'string' || !value.name.trim()) {
        throw new Error('The server returned incomplete contact details. Please refresh.');
    }
    const platform = value.id.startsWith('platform-');
    const linkedId = value.linkedClientId ?? value.linked_client_id;
    if ((expectedId && value.id !== expectedId)
        || (platform && (value.id !== `platform-${linkedId}` || !CONTACT_ID.test(value.id.slice(9))))
        || (linkedId != null && typeof linkedId !== 'string')
        || ['email', 'phoneNumber', 'phone_number', 'organization', 'notes', 'handle', 'source',
            'avatarColor', 'avatar_color', 'lastCalled', 'last_called', 'deletedAt', 'deleted_at',
            'handleUpdatedAt', 'handle_updated_at'].some(key => value[key] != null && typeof value[key] !== 'string')
        || ['isFavorite', 'is_favorite'].some(key => value[key] != null && typeof value[key] !== 'boolean')
        || ['groupIds', 'group_ids', 'groupNames', 'group_names'].some(key => !isDelimitedField(value[key]))
        || ['handleVisibility', 'handle_visibility'].some(key => value[key] != null
            && !['public', 'friends_of_friends', 'friends_only'].includes(String(value[key])))) {
        throw new Error('The server returned incomplete contact details. Please refresh.');
    }
    return normalizeContact(value);
}
export function checkedContactResponse(data, expectedId) {
    return checkedContact(isRecord(data) ? data.contact : null, expectedId);
}
export function checkedContactsResponse(data) {
    if (!isRecord(data) || !Array.isArray(data.contacts)) {
        throw new Error('The server returned an incomplete contacts list. Please refresh.');
    }
    return data.contacts.map(value => checkedContact(value));
}
/** Existing-contact updates require an explicit acknowledgement before applying the draft. */
export function confirmedSavedContact(data, draft) {
    if (!isRecord(data) || data.success !== true)
        throw new Error('The save could not be confirmed. Your edits are still here.');
    return draft;
}
export function contactDetailPath(contact) {
    return contact.id.startsWith('platform-')
        ? `/api/contacts/platform/${encodeURIComponent(contact.id.slice(9))}`
        : `/api/contacts/${encodeURIComponent(contact.id)}`;
}
export function formatDuration(secs) {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}m ${s}s`;
}
export function formatTime(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return 'Unknown time';
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
        + ` ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}
